import type { Practice } from '../types';

/**
 * Sort practices with smart ordering:
 * 1. Future practices (ascending by date)
 * 2. Past practices (descending by date)
 * 3. Highlight the current/next practice
 */
export function sortPractices(practices: Practice[]): Practice[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Separate future and past practices
  const futurePractices: Practice[] = [];
  const pastPractices: Practice[] = [];
  
  practices.forEach(practice => {
    // Parse date string and create a date object for comparison
    const [year, month, day] = practice.date.split('-').map(Number);
    const practiceDate = new Date(year, month - 1, day); // month is 0-indexed
    
    if (practiceDate >= today) {
      futurePractices.push(practice);
    } else {
      pastPractices.push(practice);
    }
  });
  
  // Sort future practices by date (ascending - next practice first)
  futurePractices.sort((a, b) => {
    const [yearA, monthA, dayA] = a.date.split('-').map(Number);
    const [yearB, monthB, dayB] = b.date.split('-').map(Number);
    return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
  });
  
  // Sort past practices by date (descending - most recent first)
  pastPractices.sort((a, b) => {
    const [yearA, monthA, dayA] = a.date.split('-').map(Number);
    const [yearB, monthB, dayB] = b.date.split('-').map(Number);
    return new Date(yearB, monthB - 1, dayB).getTime() - new Date(yearA, monthA - 1, dayA).getTime();
  });
  
  // Combine: future practices first, then past practices
  return [...futurePractices, ...pastPractices];
}

/**
 * Get the current/next practice from a sorted list
 * Returns the first future practice, or null if no future practices
 */
export function getCurrentNextPractice(practices: Practice[]): Practice | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Find the first practice that's today or in the future
  return practices.find(practice => {
    const [year, month, day] = practice.date.split('-').map(Number);
    const practiceDate = new Date(year, month - 1, day);
    return practiceDate >= today;
  }) || null;
}

/**
 * Check if a practice is today's practice (for highlighting)
 */
export function isTodayPractice(practice: Practice): boolean {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [year, month, day] = practice.date.split('-').map(Number);
  const practiceDate = new Date(year, month - 1, day);
  
  return practiceDate.getTime() === today.getTime();
}

/**
 * Check if a practice is the next practice (for "Next" label)
 */
export function isNextPractice(practice: Practice, allPractices: Practice[]): boolean {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Get all future practices sorted by date
  const futurePractices = allPractices
    .filter(p => {
      const [year, month, day] = p.date.split('-').map(Number);
      const practiceDate = new Date(year, month - 1, day);
      return practiceDate >= today;
    })
    .sort((a, b) => {
      const [yearA, monthA, dayA] = a.date.split('-').map(Number);
      const [yearB, monthB, dayB] = b.date.split('-').map(Number);
      return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
    });
  
  // The first future practice is the next practice
  return futurePractices.length > 0 && futurePractices[0].id === practice.id;
}

/**
 * Get practice status for display purposes
 */
export function getPracticeStatus(practice: Practice): {
  status: 'upcoming' | 'today' | 'past';
  label: string;
  daysUntil: number;
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [year, month, day] = practice.date.split('-').map(Number);
  const practiceDate = new Date(year, month - 1, day);
  
  const diffTime = practiceDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return {
      status: 'today',
      label: 'Today',
      daysUntil: 0
    };
  } else if (diffDays > 0) {
    return {
      status: 'upcoming',
      label: diffDays === 1 ? 'Tomorrow' : `In ${diffDays} days`,
      daysUntil: diffDays
    };
  } else {
    return {
      status: 'past',
      label: Math.abs(diffDays) === 1 ? 'Yesterday' : `${Math.abs(diffDays)} days ago`,
      daysUntil: diffDays
    };
  }
} 